/**
 * React Query hooks for QueryService operations
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryServicesApi } from '@/lib/api/queryservices';
import { tracked } from '@/lib/telemetry';
import {
  QpsWarningResponse,
  QueryService,
  QueryServiceFormData,
  QueryServiceListParams,
  QueryServiceListResponse,
  isQpsWarningResponse,
} from '../types/queryservice';
import { clusterSizeLabel, getRepresentativeExecutorCount } from '../utils/clusterSizeDerivation';

// Query keys factory for consistent cache management
export const queryServiceKeys = {
  all: ['queryservices'] as const,
  lists: () => [...queryServiceKeys.all, 'list'] as const,
  list: (params?: QueryServiceListParams) => [...queryServiceKeys.lists(), params] as const,
  details: () => [...queryServiceKeys.all, 'detail'] as const,
  detail: (name: string) => [...queryServiceKeys.details(), name] as const,
};

/**
 * Hook to fetch list of query services
 */
export function useQueryServices(params?: QueryServiceListParams) {
  return useQuery({
    queryKey: queryServiceKeys.list(params),
    queryFn: async () => {
      const response = await queryServicesApi.list(params);

      // Handle multiple response formats:
      // 1. { data: { items: [...], total } } - wrapped format
      // 2. { items: [...], total } - direct format
      // 3. [...] - array format
      const responseWithData = response as QueryServiceListResponse & {
        data?: QueryServiceListResponse;
      };
      const data = responseWithData.data || response;
      const items = data.items || (data as unknown as QueryService[]);

      // Ensure we have an array
      if (!Array.isArray(items)) {
        console.error('Invalid query service response format:', response);
        return { items: [], total: 0 };
      }

      // Normalize: attach a UI-only sizeLabel to each row so the Size column,
      // global search, and the size filter share one value. The backend stores
      // only the numeric baseSizeExecutors (PLT-8787) — the label is derived
      // here. Legacy rows without baseSizeExecutors fall back to a count derived
      // from runtime/autoscaling executor counts.
      const normalizedItems = items.map((item) => {
        if (item.sizeLabel) return item;
        const count =
          typeof item.baseSizeExecutors === 'number' && item.baseSizeExecutors > 0
            ? item.baseSizeExecutors
            : getRepresentativeExecutorCount(item);
        if (count && count > 0) {
          return { ...item, sizeLabel: clusterSizeLabel(count) };
        }
        return item;
      });

      return {
        items: normalizedItems,
        total: data.total || normalizedItems.length,
      };
    },
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single query service by name
 */
export function useQueryService(name: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryServiceKeys.detail(name),
    queryFn: () => queryServicesApi.get(name),
    enabled: options?.enabled ?? !!name,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to create a new query service
 * Supports ignoreFeedback option for QPS capacity warning bypass
 */
export function useCreateQueryService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      data,
      ignoreFeedback,
    }: {
      data: QueryServiceFormData;
      ignoreFeedback?: boolean;
    }) =>
      tracked(
        { resource: 'query-service', action: 'create', resourceName: data.name },
        (operationId) => queryServicesApi.create(data, { ignoreFeedback, operationId })
      ),
    onSuccess: (response) => {
      // Only invalidate cache if it's a real success (not a warning response)
      if (!isQpsWarningResponse(response)) {
        queryClient.invalidateQueries({ queryKey: queryServiceKeys.lists() });
      }
    },
  });
}

/**
 * Type alias for create/update mutation results that may include QPS warnings
 */
export type QueryServiceMutationResult = QueryService | QpsWarningResponse;

/**
 * Hook to update an existing query service
 * Supports ignoreFeedback option for QPS capacity warning bypass
 */
export function useUpdateQueryService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      name,
      data,
      ignoreFeedback,
    }: {
      name: string;
      data: Partial<QueryServiceFormData>;
      ignoreFeedback?: boolean;
    }) =>
      tracked({ resource: 'query-service', action: 'update', resourceName: name }, (operationId) =>
        queryServicesApi.update(name, data, { ignoreFeedback, operationId })
      ),
    onSuccess: (response, { name }) => {
      // Only invalidate cache if it's a real success (not a warning response)
      if (!isQpsWarningResponse(response)) {
        queryClient.invalidateQueries({ queryKey: queryServiceKeys.detail(name) });
        queryClient.invalidateQueries({ queryKey: queryServiceKeys.lists() });
      }
    },
  });
}

/**
 * Hook to delete a query service
 */
export function useDeleteQueryService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      tracked({ resource: 'query-service', action: 'delete', resourceName: name }, (operationId) =>
        queryServicesApi.delete(name, { operationId })
      ),
    onSuccess: (_, { name }) => {
      // Remove from cache, but only when no page is showing it. A delete from
      // the detail page succeeds while that page is still open. Removing its
      // entry then makes the page fetch the deleted cluster again before it
      // moves to the list.
      queryClient.removeQueries({
        queryKey: queryServiceKeys.detail(name),
        type: 'inactive',
      });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryServiceKeys.lists() });
    },
  });
}

/**
 * Hook to suspend a query service
 */
export function useSuspendQueryService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      tracked({ resource: 'query-service', action: 'suspend', resourceName: name }, (operationId) =>
        queryServicesApi.suspend(name, { operationId })
      ),
    onSuccess: (_, { name }) => {
      // Invalidate the specific query service and lists
      queryClient.invalidateQueries({
        queryKey: queryServiceKeys.detail(name),
      });
      queryClient.invalidateQueries({ queryKey: queryServiceKeys.lists() });
    },
  });
}

/**
 * Hook to resume a query service
 */
export function useResumeQueryService() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name }: { name: string }) =>
      tracked({ resource: 'query-service', action: 'resume', resourceName: name }, (operationId) =>
        queryServicesApi.resume(name, { operationId })
      ),
    onSuccess: (_, { name }) => {
      // Invalidate the specific query service and lists
      queryClient.invalidateQueries({
        queryKey: queryServiceKeys.detail(name),
      });
      queryClient.invalidateQueries({ queryKey: queryServiceKeys.lists() });
    },
  });
}
